/**
 * Floor-engine ownership claims.
 *
 * `useFloorEngineStore` is a GLOBAL singleton shared by every engine surface
 * (floor-plan editor, label-template designer, POS live floor). With the
 * drawer-inside-Stack navigation, a covered surface stays MOUNTED — so its
 * unmount cleanup (`clearPlan`, autosave) can fire out of order with the next
 * surface's `loadPlan` and wipe or overwrite a document it no longer owns
 * (the iPad "editor stuck panning" bug family).
 *
 * Each surface takes a monotonic claim token when it loads a document and
 * only runs its release/cleanup work when its claim is STILL the newest —
 * a newer claim means another surface owns the engine now, so the older
 * surface must stand down.
 *
 * Lives under `services/` (compiler-excluded): these are plain module
 * functions called from effect cleanups and imperative handlers.
 *
 * @module services/floorplan/engineClaim
 */

// Const-object holder (module-level `let` is banned in this codebase).
const claimState = { current: 0 };

/** Take ownership of the shared floor engine. Returns the claim token. */
export const claimFloorEngine = (): number => {
  claimState.current += 1;
  return claimState.current;
};

/** True while `claim` is still the newest claim — safe to mutate/release. */
export const isActiveFloorEngineClaim = (claim: number): boolean =>
  claimState.current === claim;
